import Phaser from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { installRetinaTextPatch } from './ui/phaserPatches';
import { GameApp } from './app/GameApp';

installRetinaTextPatch();
import { BattleScene } from './scenes/BattleScene';
import { EventScene } from './scenes/EventScene';
import { MapScene } from './scenes/MapScene';
import { TownScene } from './scenes/TownScene';
import { MinimapScene } from './scenes/MinimapScene';
import { HudScene } from './scenes/HudScene';
import { CutsceneScene } from './scenes/CutsceneScene';
import { VotingOverlay } from './scenes/VotingOverlay';
import { ChoiceOverlay } from './scenes/ChoiceOverlay';
import { CharacterCreatorScene } from './scenes/CharacterCreatorScene';
import { EquipmentOverlay } from './scenes/EquipmentOverlay';
import { ForgeOverlay } from './scenes/ForgeOverlay';
import { TavernOverlay } from './scenes/TavernOverlay';
import { GuildOverlay } from './scenes/GuildOverlay';
import { TempleOverlay } from './scenes/TempleOverlay';
import { BootScene } from './scenes/BootScene';
import { IframeBridge } from './integration/IframeBridge';
import { createTabLeadership } from './integration/tabLeadership';
import { showToast } from './ui/domHelpers';
import { fetchSession, isGameManager, type Session } from './auth/session';
import { mountAdminButton } from './ui/adminButton';
import { initUiScale } from './ui/uiScale';
import { ServerTransport } from './transport/ServerTransport';
import { EventStream } from './transport/EventStream';
import { loadCardCatalog, levelUpSteps, cardsByClass, commonCards } from './core/roscha';
import type { ActiveVote, PendingActionData, VoteResolvedData } from './transport/EventTypes';
import { BATTLE_EVENTS, REWARD_EVENTS } from './core';
import type { CutsceneSlide, RewardPayload, BattlePayload } from './core';
import './ui/styles/tokens.css'
import './ui/styles/base.css'
import './ui/styles/overlay.css'
import './ui/styles/components/character-creator.css'
import './ui/styles/components/forge.css'
import './ui/styles/components/temple.css'
import './ui/styles/components/guild.css'
import './ui/styles/components/tavern.css'
import './ui/styles/components/deck.css'
import './ui/styles/components/game-card.css'
import './ui/styles/components/equipment.css'
import './ui/styles/components/levelup.css'
import './ui/styles/components/voting.css'
import './ui/styles/components/event.css'
import './ui/styles/components/cutscene.css'
import './ui/styles/components/hud.css'
import './ui/styles/components/town.css'
import './ui/styles/components/admin-button.css'

async function waitForFonts(): Promise<void> {
  if (!document.fonts?.ready) return;
  try {
    await document.fonts.ready;
  } catch {
    
  }
}

async function checkAuth(): Promise<Session> {
  const session = await fetchSession();
  if (!session.authenticated) {
    window.location.href = import.meta.env.VITE_LOGIN_URL;
  }
  return session;
}

async function main(): Promise<void> {
  const session = await checkAuth();
  if (!session.authenticated) return;
  const apiBaseUrl = import.meta.env.VITE_GAME_SERVICE_URL;
  if (!apiBaseUrl) {
    throw new Error('VITE_GAME_SERVICE_URL is required');
  }
  await waitForFonts();
  const transport = new ServerTransport({ apiBaseUrl });

  try {
    const catalog = await transport.fetchCardCatalog();
    if (catalog.length === 0) throw new Error('card catalog is empty');
    loadCardCatalog(catalog);
  } catch (e) {
    console.error('[main] card catalog load failed:', e);
    mountCatalogError();
    return;
  }

  const leadership = createTabLeadership();

  const app = new GameApp(transport);
  app.start();
  const game = createPhaserGame(app);
  initUiScale(game);
  app.attachGame(game);

  const handleVoteResolved = (data: VoteResolvedData) => {
    console.log('[EventStream] vote_resolved:', data.winnerLabel);
    showToast(`Голосование: ${data.question} → ${data.winnerLabel}`, 'info', 4500);
    if (game.scene.isActive('VotingOverlay')) {
      app.backToMap();
    }
  };

  const debugRequested = new URLSearchParams(location.search).has('debug');
  const isLocalHost = ['localhost', '127.0.0.1'].includes(location.hostname) || location.hostname.endsWith('.localhost');
  const isInIframe = (() => { try { return window.top !== window.self; } catch { return true; } })();
  if (isGameManager(session) && !isInIframe) mountAdminButton(game);
  const debugAllowedInProd = import.meta.env.VITE_DEBUG_ENABLED === 'true' || isLocalHost;
  const debugMode = !isInIframe && (import.meta.env.DEV || (debugRequested && debugAllowedInProd));
  if (debugMode) {
    const debugWindow = window as typeof window & {
      game?: Phaser.Game;
      __circleOfDm?: {
        app: GameApp;
        game: Phaser.Game;
        dispatchPendingAction?: (a: PendingActionData) => void;
        dispatchVoteResolved?: (d: VoteResolvedData) => void;
      };
    };
    debugWindow.game = game;
    debugWindow.__circleOfDm = {
      app, game,
      dispatchPendingAction: (a: PendingActionData) => handlePendingAction(app, a),
      dispatchVoteResolved: handleVoteResolved,
    };
    setupSceneTrace(game);
  }

  new IframeBridge({ gameVersion: '0.1.0' }).attach(app);

  const gameServiceUrl = import.meta.env.VITE_GAME_SERVICE_URL;
  let eventStream: EventStream | null = null;
  if (gameServiceUrl) {
    eventStream = new EventStream(gameServiceUrl, {
      onNewVote: (_vote: ActiveVote) => {
        console.log('[EventStream] new_vote (ignored — triggered via map)');
      },
      onVoteResolved: handleVoteResolved,
      onPendingAction: (action: PendingActionData) => {
        console.log('[EventStream] pending_action:', action.kind);
        handlePendingAction(app, action);
      },
      onAuthExpired: () => {
        console.warn('[EventStream] auth expired — redirecting to login');
        if (import.meta.env.VITE_LOGIN_URL) window.location.href = import.meta.env.VITE_LOGIN_URL;
      },
    }, {
      authCheckUrl: import.meta.env.VITE_API_BASE ? `${import.meta.env.VITE_API_BASE}/v1/auth/me` : undefined,
    });
  }

  game.events.once(Phaser.Core.Events.READY, () => {
    let passiveOverlay: { destroy: () => void } | null = null;
    leadership.onChange((isLeader) => {
      if (isLeader) {
        passiveOverlay?.destroy();
        passiveOverlay = null;
        game.loop.wake();
        eventStream?.connect();
      } else {
        eventStream?.disconnect();
        game.loop.sleep();
        if (!passiveOverlay) passiveOverlay = mountLeadershipOverlay(() => leadership.claim());
      }
    });
  });

  if (import.meta.env.DEV) {
    setupDevHotkeys(app, game);
  }
}

function mountCatalogError() {
  const el = document.createElement('div');
  el.style.cssText = 'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;background:#0b0e14;color:#e6e2d3;font:600 1rem system-ui,sans-serif;z-index:99999;text-align:center;padding:2rem;';
  el.innerHTML = '<div style="font-size:1.25rem;color:#c8a04a;">Не удалось загрузить карты</div><div style="opacity:0.8;max-width:32rem;">Каталог карт недоступен. Проверь соединение и перезагрузи страницу.</div><button style="margin-top:0.5rem;padding:0.6rem 1.4rem;border-radius:0.5rem;border:1px solid #c8a04a;background:transparent;color:#c8a04a;cursor:pointer;font:inherit;">Перезагрузить</button>';
  el.querySelector('button')?.addEventListener('click', () => window.location.reload());
  document.body.appendChild(el);
}

function mountLeadershipOverlay(onClaim: () => void): { destroy: () => void } {
  const div = document.createElement('div');
  div.className = 'dom-leadership';
  div.innerHTML = `
    <div class="dom-leadership__card">
      <div class="dom-leadership__title">Игра открыта в другой вкладке</div>
      <div class="dom-leadership__text">Управление сейчас в другой вкладке. Нажми «Играть здесь» — управление перейдёт сюда, а другая вкладка встанет на паузу.</div>
      <button class="dom-leadership__btn" type="button">Играть здесь</button>
    </div>
  `;
  document.body.appendChild(div);
  div.querySelector('button')?.addEventListener('click', onClaim);
  return { destroy: () => div.remove() };
}

function setupSceneTrace(game: Phaser.Game) {
  game.scene.scenes.forEach((scene) => {
    const key = scene.scene.key;
    scene.events.on('start', () => console.log(`[scene] start: ${key}`));
    scene.events.on('shutdown', () => console.log(`[scene] shutdown: ${key}`));
    scene.events.on('sleep', () => console.log(`[scene] sleep: ${key}`));
    scene.events.on('wake', () => console.log(`[scene] wake: ${key}`));
    scene.events.on('pause', () => console.log(`[scene] pause: ${key}`));
    scene.events.on('resume', () => console.log(`[scene] resume: ${key}`));
  });
}

const appliedPendingActionIds = new Set<string>();
const MAX_APPLIED_PENDING_IDS = 200;

function rememberAppliedPendingId(id: string) {
  if (appliedPendingActionIds.size >= MAX_APPLIED_PENDING_IDS) {
    const oldest = appliedPendingActionIds.values().next().value;
    if (oldest !== undefined) appliedPendingActionIds.delete(oldest);
  }
  appliedPendingActionIds.add(id);
}

function handlePendingAction(app: GameApp, action: PendingActionData) {
  if (action.id && appliedPendingActionIds.has(action.id)) {
    console.log('[EventStream] duplicate pending_action ignored:', action.id);
    return;
  }
  if (action.id) rememberAppliedPendingId(action.id);
  switch (action.kind) {
    case 'grant_resources': {
      const state = app.getWorldState();
      if (state) {
        const payload = action.payload as Record<string, number>;
        for (const [resource, delta] of Object.entries(payload)) {
          const current = state.hero.inventory.resources[resource as 'gold' | 'mana'] ?? 0;
          state.hero.inventory.resources[resource as 'gold' | 'mana'] = Math.max(0, current + delta);
        }
        app.emitWorldState();
      }
      console.log('[GM] Resources granted:', action.payload);
      break;
    }
    case 'trigger_event': {
      const payload = action.payload as { kind: string };
      if (payload.kind === 'battle') {
        app.openBattle(payload as unknown as BattlePayload);
      } else if (payload.kind === 'reward') {
        app.openEvent(payload as unknown as RewardPayload);
      }
      break;
    }
    case 'trigger_cutscene': {
      const payload = action.payload as { slides: CutsceneSlide[] };
      app.openCutscene(payload.slides);
      break;
    }
    case 'vote_result': {
      console.log('[GM] Vote result:', action.payload);
      break;
    }
  }
}

function createPhaserGame(app: GameApp) {
  const root = document.getElementById('game-root');
  if (!root) throw new Error('Missing #game-root');

  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.WEBGL,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      parent: root,
      width: 1920,
      height: 1080,
    },
    dom : { createContainer: true },
    backgroundColor: '#080c14',
    plugins: {
      scene: [
        { key: 'rexUI', plugin: UIPlugin, mapping: 'rexUI' },
      ],
    },
    scene: [
      BootScene,
      MapScene, MinimapScene, HudScene,
      TownScene, BattleScene, EventScene, CutsceneScene, VotingOverlay, ChoiceOverlay,
      CharacterCreatorScene, EquipmentOverlay, ForgeOverlay, TavernOverlay, GuildOverlay, TempleOverlay,
    ],
  };

  const game = new Phaser.Game(config);
  game.registry.set('app', app);

  return game;
}

function setupDevHotkeys(app: GameApp, game: Phaser.Game) {
  let battleIdx = 0;

  window.addEventListener('keydown', (e) => {
    if (e.key === 'b' || e.key === 'B') {
      const battle = BATTLE_EVENTS[battleIdx % BATTLE_EVENTS.length];
      battleIdx++;
      const world = app.getWorldState();
      if (world?.hero) {
        world.hero.hp = world.hero.maxHp ?? 30;
      }
      console.log(`[DEV] Battle: ${battle.enemyTemplateId} Ур 5, 100 HP (#${battleIdx})`);
      app.openBattle({ kind: 'battle', ...battle, enemyLevel: 5, enemyMaxHp: 100 });
    }

    if (e.key === 'r' || e.key === 'R') {
      const reward = REWARD_EVENTS[Math.floor(Math.random() * REWARD_EVENTS.length)];
      console.log(`[DEV] Reward event`);
      app.openEvent({ kind: 'reward', ...reward });
    }

    if (e.key === 'n' || e.key === 'N') {
      console.log('[DEV] Resetting save and reloading');
      app.resetSaveAndReload();
    }

    if (e.key === 's' || e.key === 'S') {
      const scenes = game.scene.scenes.map((s) => ({
        key: s.scene.key,
        active: s.scene.isActive(),
        visible: s.scene.isVisible(),
        sleeping: s.scene.isSleeping(),
      }));
      console.table(scenes);
    }

    if (e.key === 'l' || e.key === 'L') {
      const hero = app.getWorldState()?.hero;
      if (!hero || !hero.classId || !hero.domainId) return;
      const steps = levelUpSteps(1, [...cardsByClass(hero.classId), ...commonCards()]);
      console.log('[DEV] LevelUp');
      app.openChoice(steps, (choice) => app.applyLevelUpChoice(choice), () => app.advanceHeroLevel());
    }

    if (e.key === 'c' || e.key === 'C') {
      console.log('[DEV] Cutscene');
      app.openCutscene([
        { speaker: 'Старейшина', text: 'Юный герой, рощи зовут тебя. Древнее зло пробудилось.' },
        { speaker: 'Старейшина', text: 'Возьми этот клинок. Он служил многим до тебя, и теперь он твой.' },
        { speaker: '', text: 'Путь будет нелёгким. Но дорога начинается с первого шага...' },
      ]);
    }

    if (e.key === 'j' || e.key === 'J') {
      const world = app.getWorldState();
      if (world?.hero) {
        world.hero.hp = world.hero.maxHp ?? 30;
      }
      console.log('[DEV] Wolf jumper battle (unbreakable test)');
      app.openBattle({
        kind: 'battle',
        text: 'Волк-прыгун несётся прямо на тебя!',
        enemyTemplateId: 'wolf_jumper',
        enemyLevel: 1,
        enemyMaxHp: 40,
        loot: { itemId: 'wolf_pelt', itemName: 'Шкура волка', description: 'Тёплая.', cardRewards: [] },
      });
    }
  });

  console.log('[DEV] Hotkeys: B = battle, R = reward, L = level up, C = cutscene, N = new game, S = list scenes, J = wolf jumper (unbreakable test)');
}

main();